import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Search,
  Filter,
  Upload,
  MoreHorizontal,
  Eye,
  Download,
  BookOpen,
  Share2,
  Trash2,
  Database,
  FileCode,
  Scale,
  Beaker,
  FileText,
  Image,
  GraduationCap,
  FileBarChart,
  FileQuestion,
  Calendar,
  Users,
  Building2,
  Tag,
  ExternalLink,
  Copy,
  Grid3X3,
  List,
  SlidersHorizontal,
  Globe,
  Lock,
  Users2,
  Building,
  Sparkles,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import type { ResearchResource, ResourceType, ResourceStatus, AccessLevel } from '@/types/research';
import { cn } from '@/lib/utils';

const RESOURCE_TYPE_CONFIG: Record<ResourceType, { icon: React.ReactNode; color: string; label: string }> = {
  article: { icon: <BookOpen className="h-4 w-4" />, color: 'bg-blue-500', label: 'Article' },
  dataset: { icon: <Database className="h-4 w-4" />, color: 'bg-green-500', label: 'Dataset' },
  software: { icon: <FileCode className="h-4 w-4" />, color: 'bg-purple-500', label: 'Software' },
  patent: { icon: <Scale className="h-4 w-4" />, color: 'bg-orange-500', label: 'Patent' },
  lab_tool: { icon: <Beaker className="h-4 w-4" />, color: 'bg-pink-500', label: 'Lab Tool' },
  manual: { icon: <FileText className="h-4 w-4" />, color: 'bg-cyan-500', label: 'Manual' },
  image: { icon: <Image className="h-4 w-4" />, color: 'bg-rose-500', label: 'Image' },
  thesis: { icon: <GraduationCap className="h-4 w-4" />, color: 'bg-indigo-500', label: 'Thesis' },
  report: { icon: <FileBarChart className="h-4 w-4" />, color: 'bg-teal-500', label: 'Report' },
  grant_proposal: { icon: <FileQuestion className="h-4 w-4" />, color: 'bg-amber-500', label: 'Grant Proposal' },
};

const STATUS_CONFIG: Record<ResourceStatus, { color: string; label: string }> = {
  draft: { color: 'bg-gray-500', label: 'Draft' },
  published: { color: 'bg-green-500', label: 'Published' },
  embargoed: { color: 'bg-yellow-500', label: 'Embargoed' },
  archived: { color: 'bg-slate-500', label: 'Archived' },
  under_review: { color: 'bg-blue-500', label: 'Under Review' },
};

const ACCESS_LEVEL_CONFIG: Record<AccessLevel, { icon: React.ReactNode; label: string }> = {
  private: { icon: <Lock className="h-3 w-3" />, label: 'Private' },
  team: { icon: <Users2 className="h-3 w-3" />, label: 'Team' },
  department: { icon: <Building className="h-3 w-3" />, label: 'Department' },
  institution: { icon: <Building2 className="h-3 w-3" />, label: 'Institution' },
  public: { icon: <Globe className="h-3 w-3" />, label: 'Public' },
};

export function ResearchResourcesPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedResource, setSelectedResource] = useState<ResearchResource | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['research-resources', searchQuery, typeFilter, statusFilter],
    queryFn: () =>
      researchService.listResources({
        search: searchQuery || undefined,
        type: typeFilter !== 'all' ? (typeFilter as ResourceType) : undefined,
        status: statusFilter !== 'all' ? (statusFilter as ResourceStatus) : undefined,
      }),
  });

  const resources = data?.data || [];

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Research Resources</h1>
          <p className="text-muted-foreground">
            Browse, search, and manage research resources across all categories
          </p>
        </div>
        <Button onClick={() => setShowUploadDialog(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Upload Resource
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search resources by title, keywords, authors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[160px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Object.entries(RESOURCE_TYPE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {config.icon}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]">
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="icon"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-medium">{resources.length}</span> resources
        </p>
      </div>

      {/* Resources Grid/List */}
      {isLoading ? (
        <div className={cn(
          'grid gap-4',
          viewMode === 'grid' ? 'md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
        )}>
          {[...Array(6)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-6 w-3/4 mb-2" />
                <Skeleton className="h-4 w-full mb-4" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {resources.map((resource) => {
            const typeConfig = RESOURCE_TYPE_CONFIG[resource.type];
            const statusConfig = STATUS_CONFIG[resource.status];
            const accessConfig = ACCESS_LEVEL_CONFIG[resource.accessLevel];

            return (
              <Card
                key={resource.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:border-primary/50 group"
                onClick={() => setSelectedResource(resource)}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className={cn('flex h-10 w-10 items-center justify-center rounded-lg text-white', typeConfig.color)}>
                      {typeConfig.icon}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="mr-2 h-4 w-4" />
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Download className="mr-2 h-4 w-4" />
                          Download
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <BookOpen className="mr-2 h-4 w-4" />
                          Generate Citation
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Share2 className="mr-2 h-4 w-4" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-destructive">
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <CardTitle className="text-base leading-tight line-clamp-2 mt-3">
                    {resource.title}
                  </CardTitle>
                  <CardDescription className="line-clamp-2">
                    {resource.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {/* Authors */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{resource.authors.slice(0, 2).join(', ')}</span>
                      {resource.authors.length > 2 && (
                        <span className="text-xs">+{resource.authors.length - 2}</span>
                      )}
                    </div>

                    {/* Institutions */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Building2 className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{resource.institutions[0]}</span>
                    </div>

                    {/* Date */}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4 flex-shrink-0" />
                      <span>{formatDate(resource.publicationDate || resource.createdAt)}</span>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      {resource.keywords.slice(0, 3).map((keyword) => (
                        <Badge key={keyword} variant="secondary" className="text-xs">
                          {keyword}
                        </Badge>
                      ))}
                      {resource.keywords.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{resource.keywords.length - 3}
                        </Badge>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={cn('text-xs', statusConfig.color.replace('bg-', 'border-'))}>
                          <span className={cn('h-1.5 w-1.5 rounded-full mr-1', statusConfig.color)} />
                          {statusConfig.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {accessConfig.icon}
                          <span className="ml-1">{accessConfig.label}</span>
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {resource.viewCount.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Download className="h-3 w-3" />
                          {resource.downloadCount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {resources.map((resource) => {
            const typeConfig = RESOURCE_TYPE_CONFIG[resource.type];
            const statusConfig = STATUS_CONFIG[resource.status];

            return (
              <Card
                key={resource.id}
                className="cursor-pointer hover:shadow-md transition-all hover:border-primary/50"
                onClick={() => setSelectedResource(resource)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className={cn('flex h-12 w-12 items-center justify-center rounded-lg text-white flex-shrink-0', typeConfig.color)}>
                      {typeConfig.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground truncate">
                        {resource.authors.join(', ')} • {resource.institutions[0]}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <Badge variant="outline" className="text-xs">
                        <span className={cn('h-1.5 w-1.5 rounded-full mr-1', statusConfig.color)} />
                        {statusConfig.label}
                      </Badge>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(resource.publicationDate || resource.createdAt)}
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {resource.viewCount.toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          {resource.citationCount}
                        </span>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <BookOpen className="mr-2 h-4 w-4" />
                            Generate Citation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Resource Detail Dialog */}
      <Dialog open={!!selectedResource} onOpenChange={() => setSelectedResource(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedResource && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <div className={cn(
                    'flex h-12 w-12 items-center justify-center rounded-lg text-white flex-shrink-0',
                    RESOURCE_TYPE_CONFIG[selectedResource.type].color
                  )}>
                    {RESOURCE_TYPE_CONFIG[selectedResource.type].icon}
                  </div>
                  <div>
                    <DialogTitle className="text-xl leading-tight">
                      {selectedResource.title}
                    </DialogTitle>
                    <DialogDescription className="mt-2">
                      {selectedResource.description}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Abstract */}
                {selectedResource.abstract && (
                  <div>
                    <h4 className="font-semibold mb-2">Abstract</h4>
                    <p className="text-sm text-muted-foreground">{selectedResource.abstract}</p>
                  </div>
                )}

                {/* Metadata Grid */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Authors</Label>
                      <p className="text-sm">{selectedResource.authors.join(', ')}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Institutions</Label>
                      <p className="text-sm">{selectedResource.institutions.join(', ')}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Publication Date</Label>
                      <p className="text-sm">{formatDate(selectedResource.publicationDate)}</p>
                    </div>
                    {selectedResource.doi && (
                      <div>
                        <Label className="text-xs text-muted-foreground">DOI</Label>
                        <p className="text-sm flex items-center gap-2">
                          {selectedResource.doi}
                          <ExternalLink className="h-3 w-3 cursor-pointer text-primary" />
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-3">
                    {selectedResource.grantNumber && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Grant Number</Label>
                        <p className="text-sm">{selectedResource.grantNumber}</p>
                      </div>
                    )}
                    {selectedResource.fundingSource && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Funding Source</Label>
                        <p className="text-sm">{selectedResource.fundingSource}</p>
                      </div>
                    )}
                    {selectedResource.methodology && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Methodology</Label>
                        <p className="text-sm">{selectedResource.methodology}</p>
                      </div>
                    )}
                    {selectedResource.fileSize && (
                      <div>
                        <Label className="text-xs text-muted-foreground">File Size</Label>
                        <p className="text-sm">{formatFileSize(selectedResource.fileSize)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Keywords */}
                <div>
                  <Label className="text-xs text-muted-foreground">Keywords</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedResource.keywords.map((keyword) => (
                      <Badge key={keyword} variant="secondary">
                        <Tag className="mr-1 h-3 w-3" />
                        {keyword}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* ANZSRC Codes */}
                <div>
                  <Label className="text-xs text-muted-foreground">Research Classification (ANZSRC)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedResource.anzsrcCodes.map((code) => (
                      <Badge key={code} variant="outline">
                        {code}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-lg">
                  <div className="text-center">
                    <p className="text-2xl font-bold">{selectedResource.viewCount.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Views</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{selectedResource.downloadCount.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Downloads</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold">{selectedResource.citationCount}</p>
                    <p className="text-xs text-muted-foreground">Citations</p>
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedResource(null)}>
                  Close
                </Button>
                <Button variant="outline">
                  <BookOpen className="mr-2 h-4 w-4" />
                  Cite
                </Button>
                <Button variant="outline">
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
                <Button>
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Research Resource</DialogTitle>
            <DialogDescription>
              Upload a new research resource to the data center. AI will automatically extract metadata.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Drop Zone */}
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer">
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
              <p className="font-medium">Drop files here or click to browse</p>
              <p className="text-sm text-muted-foreground mt-1">
                Supports PDF, Word, Excel, ZIP, and common image formats
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Resource Type</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RESOURCE_TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          {config.icon}
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Access Level</Label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select access level" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACCESS_LEVEL_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          {config.icon}
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Title</Label>
              <Input placeholder="Resource title (auto-extracted from file)" />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea placeholder="Brief description of the resource" rows={3} />
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <Sparkles className="h-5 w-5" />
                <span className="font-medium">AI Auto-Cataloging</span>
              </div>
              <p className="text-sm text-blue-600/80 dark:text-blue-400/80 mt-1">
                Our AI will automatically extract authors, keywords, methodology, and classify your resource using ANZSRC codes.
              </p>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
              Cancel
            </Button>
            <Button>
              <Upload className="mr-2 h-4 w-4" />
              Upload & Process
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

